import { ReactNode, useCallback, useRef, useState } from 'react';

import { Box, useTheme } from '@sbv-clients/ui-shared';

export const ResizablePanel = ({
  children,
  defaultHeight = 300,
  minHeight = 100,
}: {
  children: ReactNode;
  defaultHeight?: number;
  minHeight?: number;
}) => {
  const theme = useTheme();
  const [height, setHeight] = useState(defaultHeight);
  const dragState = useRef({ isDragging: false, startY: 0, startHeight: 0 });

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragState.current = {
        isDragging: true,
        startY: e.clientY,
        startHeight: height,
      };

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragState.current.isDragging) return;
        const delta = ev.clientY - dragState.current.startY;
        setHeight(Math.max(minHeight, dragState.current.startHeight + delta));
      };

      const onMouseUp = () => {
        dragState.current.isDragging = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [height, minHeight]
  );

  return (
    <Box>
      <Box sx={{ height, overflow: 'hidden' }}>{children}</Box>
      <Box
        onMouseDown={onMouseDown}
        sx={{
          height: '6px',
          cursor: 'ns-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.divider,
          userSelect: 'none',
          borderRadius: '10px',
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
            '&::after': {
              backgroundColor: theme.palette.primary.contrastText,
            },
          },
          '&::after': {
            content: '""',
            display: 'block',
            width: '40px',
            height: '2px',
            borderRadius: '2px',
            backgroundColor: theme.palette.grey[500],
          },
        }}
      />
    </Box>
  );
};
